/**
 * Combo Analytics (dev-only page)
 *
 * Tab 1 — Stock View: pick a symbol, see its price chart + top/bottom setups.
 *                     Hover a setup to highlight the dates it was active on
 *                     the chart (as a histogram ribbon below the price line).
 * Tab 2 — Matrix View: stocks × universal top/bottom combos, cells = delta_pnl.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SetupIcon, displayCell } from '../common/SetupIcon';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  LineSeries,
  HistogramSeries,
  LineData,
  HistogramData,
  Time,
  CrosshairMode,
} from 'lightweight-charts';
import { apiService } from '../../services/api';
import Layout from '../common/Layout';

type Tab = 'stock' | 'matrix' | 'regimes' | 'regime_tree' | 'regime_gbr' | 'fingerprint';

interface Setup {
  combo: string;
  factor_count: number;
  factor_a: string | null;
  factor_b: string | null;
  factor_c: string | null;
  n_samples: number;
  avg_pnl_pct: number;
  win_rate_pct: number;
  delta_pnl: number;
  delta_win: number;
  synergy: number | null;
}

interface SetupsResponse {
  symbol: string;
  baseline_pnl: number;
  baseline_win: number;
  n_total: number;
  top: Setup[];
  bottom: Setup[];
}

interface MatrixCell {
  delta_pnl: number;
  n_samples: number;
  avg_pnl_pct: number;
  win_rate_pct: number;
}

interface MatrixRow {
  symbol: string;
  baseline_pnl: number;
  cells: Record<string, MatrixCell | null>;
}

interface MatrixResponse {
  factor_count: number;
  top_columns: Array<{ combo: string; appearances: number }>;
  bottom_columns: Array<{ combo: string; appearances: number }>;
  rows: MatrixRow[];
}

const COLORS = {
  bg: '#0f1419',
  panel: '#1a1f2b',
  border: '#2a3142',
  text: '#e5e7eb',
  textDim: '#9ca3af',
  textMuted: '#6b7280',
  good: '#10b981',
  bad: '#ef4444',
  accent: '#3b82f6',
};

const fmtPct = (n: number, decimals = 2) =>
  `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}%`;

const ComboAnalytics: React.FC = () => {
  const [tab, setTab] = useState<Tab>('stock');
  const [symbols, setSymbols] = useState<string[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [highlightableFactors, setHighlightableFactors] = useState<string[]>([]);

  // Load symbol list from fingerprint data (489 stocks, cached on backend)
  useEffect(() => {
    let cancelled = false;
    apiService
      .getComboAnalyticsFingerprint()
      .then((res) => {
        if (cancelled) return;
        const syms = Object.keys(res.stocks).sort();
        setSymbols(syms);
        if (syms.length > 0) setSelectedSymbol(syms[0]);
      })
      .catch((err) => console.error('Failed to load fingerprint for symbol list', err));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Layout>
      <div
        style={{
          padding: '12px 24px',
          height: '100vh',
          boxSizing: 'border-box',
          color: COLORS.text,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 12,
            borderBottom: `1px solid ${COLORS.border}`,
            flexShrink: 0,
          }}
        >
          {([
            { id: 'stock', label: 'Stock View' },
            { id: 'matrix', label: 'Matrix View' },
            { id: 'regimes', label: 'Regimes' },
            { id: 'regime_tree', label: 'Regime Tree' },
            { id: 'regime_gbr', label: 'Gradient Boosted' },
            { id: 'fingerprint', label: 'Fingerprint' },
          ] as Array<{ id: Tab; label: string }>).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '8px 16px',
                background: tab === t.id ? COLORS.panel : 'transparent',
                color: tab === t.id ? COLORS.text : COLORS.textDim,
                border: 'none',
                borderBottom: tab === t.id ? `2px solid ${COLORS.accent}` : '2px solid transparent',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {tab === 'stock' && (
            <StockTab
              symbols={symbols}
              selectedSymbol={selectedSymbol}
              onSelectSymbol={setSelectedSymbol}
              highlightableFactors={highlightableFactors}
            />
          )}
          {tab === 'matrix' && <MatrixTab />}
          {tab === 'regimes' && <RegimesTab />}
          {tab === 'regime_tree' && <RegimeTreeTab />}
          {tab === 'regime_gbr' && <RegimeGbrTab />}
          {tab === 'fingerprint' && <FingerprintTab />}
        </div>
      </div>
    </Layout>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Stock View Tab
// ═══════════════════════════════════════════════════════════════════════════

type SetupSubTab = 'bypnl' | 'byvolume' | 'builder';

const HV_OPTIONS = ['LO', 'MED', 'HI'] as const;
const SLOPE_OPTIONS = ['DOWN', 'FLAT', 'UP'] as const;
const CONSOL_OPTIONS = ['EXPANDING', 'NORMAL', 'TIGHT'] as const;
const HV_LABELS: Record<string, string> = { LO: 'Calm', MED: 'Moderate', HI: 'Volatile' };
const SLOPE_LABELS: Record<string, string> = { DOWN: 'Declining', FLAT: 'Flat', UP: 'Rising' };
const CONSOL_LABELS: Record<string, string> = { EXPANDING: 'Expanding', NORMAL: 'Normal', TIGHT: 'Coiled' };


interface CellSetup {
  cell_id: string;
  n: number;
  avg_pnl: number | null;
  win_rate: number | null;
  delta_pnl: number | null;
  delta_win: number | null;
  label: string;
  is_current: boolean;
}

// P&L distribution histogram for a given cell (raw P&L, not delta).
// Renders inline SVG bars, 20 bins across data range with 0-line marker.
const PnlHistogram: React.FC<{
  cellId: string;
  bars: Array<{ cell_id: string; pnl: number | null }>;
  barsVersion: number;
}> = ({ cellId, bars }) => {
  const { pnls, allPnls } = useMemo(() => {
    const all: number[] = [];
    const cell: number[] = [];
    for (const b of bars) {
      if (b.pnl === null) continue;
      const v = b.pnl as number;
      all.push(v);
      if (b.cell_id === cellId) cell.push(v);
    }
    return { pnls: cell, allPnls: all };
  }, [bars, cellId]);

  if (pnls.length === 0) {
    return (
      <div style={{ fontSize: 10, color: COLORS.textMuted, fontStyle: 'italic', padding: 8 }}>
        No P&L samples available for this cell.
      </div>
    );
  }

  // Use the ALL-setups range so foreground sits in context of the full distribution
  const rangePool = allPnls.length > 0 ? allPnls : pnls;
  const minV = Math.min(...rangePool);
  const maxV = Math.max(...rangePool);
  const pad = Math.max(0.25, (maxV - minV) * 0.02);
  const lo = minV - pad;
  const hi = maxV + pad;
  const nBins = 20;
  const step = (hi - lo) / nBins;

  const binIdx = (v: number) =>
    Math.min(nBins - 1, Math.max(0, Math.floor((v - lo) / step)));
  const countsFg = new Array(nBins).fill(0);
  const countsBg = new Array(nBins).fill(0);
  for (const v of pnls) countsFg[binIdx(v)]++;
  for (const v of allPnls) countsBg[binIdx(v)]++;

  // Normalize to % of own N so both series are comparable on a single axis
  const fgPct = countsFg.map((c) => (c / pnls.length) * 100);
  const bgPct = allPnls.length > 0
    ? countsBg.map((c) => (c / allPnls.length) * 100)
    : countsBg.map(() => 0);
  const maxPct = Math.max(...fgPct, ...bgPct, 1);

  // Cell-specific stats
  const cellMean = pnls.reduce((s, v) => s + v, 0) / pnls.length;
  const cellMedian = (() => {
    const sorted = [...pnls].sort((a, b) => a - b);
    const m = sorted.length;
    return m % 2 ? sorted[(m - 1) / 2] : (sorted[m / 2 - 1] + sorted[m / 2]) / 2;
  })();
  const cellWin = (pnls.filter((v) => v > 0).length / pnls.length) * 100;
  const cellMin = Math.min(...pnls);
  const cellMax = Math.max(...pnls);
  // Baseline (all setups) mean for reference
  const bgMean = allPnls.length > 0
    ? allPnls.reduce((s, v) => s + v, 0) / allPnls.length
    : null;

  const width = 340;
  const height = 110;
  const padL = 30;
  const padR = 8;
  const padT = 8;
  const padB = 18;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;
  const xFor = (v: number) => padL + ((v - lo) / (hi - lo)) * plotW;
  const barW = plotW / nBins;

  // Axis ticks: min, 0 (if in range), max
  const ticks: number[] = [lo];
  if (lo < 0 && hi > 0) ticks.push(0);
  ticks.push(hi);

  return (
    <div style={{
      background: '#0f1420',
      border: `1px solid ${COLORS.border}`,
      borderRadius: 6,
      padding: 8,
    }}>
      <div style={{
        display: 'flex',
        gap: 12,
        fontSize: 10,
        color: COLORS.textMuted,
        marginBottom: 4,
        flexWrap: 'wrap',
      }}>
        <span>N=<b style={{ color: COLORS.text }}>{pnls.length}</b></span>
        <span>Mean=<b style={{ color: cellMean >= 0 ? COLORS.good : COLORS.bad }}>{fmtPct(cellMean)}</b></span>
        <span>Median=<b style={{ color: cellMedian >= 0 ? COLORS.good : COLORS.bad }}>{fmtPct(cellMedian)}</b></span>
        <span>Win=<b style={{ color: COLORS.text }}>{cellWin.toFixed(0)}%</b></span>
        <span>Min=<b style={{ color: COLORS.bad }}>{fmtPct(cellMin)}</b></span>
        <span>Max=<b style={{ color: COLORS.good }}>{fmtPct(cellMax)}</b></span>
        {bgMean !== null && (
          <span style={{ marginLeft: 'auto' }}>
            vs baseline <b style={{ color: bgMean >= 0 ? COLORS.good : COLORS.bad }}>{fmtPct(bgMean)}</b>
            <span style={{ color: COLORS.textMuted }}> (N={allPnls.length})</span>
          </span>
        )}
      </div>
      <svg width={width} height={height} style={{ display: 'block' }}>
        {/* zero line */}
        {lo < 0 && hi > 0 && (
          <line
            x1={xFor(0)} x2={xFor(0)}
            y1={padT} y2={padT + plotH}
            stroke={COLORS.textMuted}
            strokeWidth={1}
            strokeDasharray="2 2"
          />
        )}
        {/* background bars: all setups (muted gray) */}
        {bgPct.map((pct, i) => {
          if (pct === 0) return null;
          const h = (pct / maxPct) * plotH;
          return (
            <rect
              key={`bg-${i}`}
              x={padL + i * barW + 0.5}
              y={padT + plotH - h}
              width={Math.max(1, barW - 1)}
              height={h}
              fill={COLORS.textMuted}
              opacity={0.25}
            />
          );
        })}
        {/* foreground bars: this cell only (red/green) */}
        {fgPct.map((pct, i) => {
          if (pct === 0) return null;
          const binCenter = lo + (i + 0.5) * step;
          const h = (pct / maxPct) * plotH;
          return (
            <rect
              key={`fg-${i}`}
              x={padL + i * barW + 0.5}
              y={padT + plotH - h}
              width={Math.max(1, barW - 1)}
              height={h}
              fill={binCenter >= 0 ? COLORS.good : COLORS.bad}
              opacity={0.85}
            />
          );
        })}
        {/* baseline mean marker (dashed gray) */}
        {bgMean !== null && (
          <line
            x1={xFor(bgMean)} x2={xFor(bgMean)}
            y1={padT} y2={padT + plotH}
            stroke={COLORS.textDim}
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}
        {/* cell mean marker (solid blue) */}
        <line
          x1={xFor(cellMean)} x2={xFor(cellMean)}
          y1={padT} y2={padT + plotH}
          stroke={COLORS.accent}
          strokeWidth={1.5}
        />
        {/* y=0 baseline */}
        <line
          x1={padL} x2={padL + plotW}
          y1={padT + plotH} y2={padT + plotH}
          stroke={COLORS.border}
          strokeWidth={1}
        />
        {/* x-axis ticks */}
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={xFor(t)} x2={xFor(t)}
              y1={padT + plotH} y2={padT + plotH + 3}
              stroke={COLORS.textMuted}
              strokeWidth={1}
            />
            <text
              x={xFor(t)}
              y={padT + plotH + 12}
              fill={COLORS.textMuted}
              fontSize={9}
              textAnchor="middle"
            >
              {t === 0 ? '0' : fmtPct(t, 1)}
            </text>
          </g>
        ))}
        {/* y-axis: max % */}
        <text
          x={padL - 4}
          y={padT + 8}
          fill={COLORS.textMuted}
          fontSize={9}
          textAnchor="end"
        >
          {maxPct.toFixed(0)}%
        </text>
        <text
          x={padL - 4}
          y={padT + plotH}
          fill={COLORS.textMuted}
          fontSize={9}
          textAnchor="end"
        >
          0
        </text>
      </svg>
      <div style={{
        display: 'flex',
        gap: 10,
        fontSize: 9,
        color: COLORS.textMuted,
        marginTop: 4,
      }}>
        <span><span style={{
          display: 'inline-block', width: 8, height: 8,
          background: COLORS.textMuted, opacity: 0.25, marginRight: 3,
        }}/>all setups</span>
        <span><span style={{
          display: 'inline-block', width: 8, height: 8,
          background: COLORS.good, opacity: 0.85, marginRight: 3,
        }}/>this cell</span>
        <span><span style={{
          display: 'inline-block', width: 10, height: 2,
          background: COLORS.accent, marginRight: 3, verticalAlign: 'middle',
        }}/>cell mean</span>
        <span><span style={{
          display: 'inline-block', width: 10, height: 0,
          borderTop: `1px dashed ${COLORS.textDim}`, marginRight: 3, verticalAlign: 'middle',
        }}/>baseline</span>
      </div>
    </div>
  );
};

const StockTab: React.FC<{
  symbols: string[];
  selectedSymbol: string;
  onSelectSymbol: (s: string) => void;
  highlightableFactors: string[];
}> = ({ symbols, selectedSymbol, onSelectSymbol }) => {
  const [cellSetups, setCellSetups] = useState<CellSetup[]>([]);
  const [baseline, setBaseline] = useState<{ pnl: number; win: number; n: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [hoveredCombo, setHoveredCombo] = useState<string | null>(null);
  const [expandedCell, setExpandedCell] = useState<string | null>(null);
  const [barsVersion, setBarsVersion] = useState(0);
  const [subTab, setSubTab] = useState<SetupSubTab>('bypnl');
  const [builderHV, setBuilderHV] = useState<string | null>(null);
  const [builderSlope, setBuilderSlope] = useState<string | null>(null);
  const [builderConsol, setBuilderConsol] = useState<string | null>(null);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const priceSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const highlightSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const hvSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const slopeSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const consolSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const tercileLinesAdded = useRef(false);
  const indicatorBarsRef = useRef<Array<{ date: string; close: number; hv_20: number; slope_12w: number; consol_z: number; cell_id: string; pnl: number | null }>>([]);

  // Load fingerprint cell data when selectedSymbol changes
  useEffect(() => {
    if (!selectedSymbol) return;
    let cancelled = false;
    setLoading(true);
    setCellSetups([]);
    setBaseline(null);
    setHoveredCombo(null);
    setExpandedCell(null);
    apiService
      .getComboAnalyticsFingerprint()
      .then((res) => {
        if (cancelled) return;
        const stock = res.stocks[selectedSymbol];
        if (!stock) return;
        setBaseline({ pnl: stock.baseline_pnl, win: stock.baseline_win, n: stock.n_total });
        const cells: CellSetup[] = Object.entries(stock.cells).map(([cell_id, c]: [string, any]) => ({
          cell_id,
          n: c.n,
          avg_pnl: c.avg_pnl,
          win_rate: c.win_rate,
          delta_pnl: c.delta_pnl,
          delta_win: c.delta_win,
          label: c.label,
          is_current: stock.current_cell === cell_id,
        }));
        setCellSetups(cells);
      })
      .catch((err) => console.error('Failed to load fingerprint', err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedSymbol]);

  // Create chart with 4 panes: price + highlight | HV | slope | consol
  useEffect(() => {
    if (!chartContainerRef.current) return;
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      autoSize: true,
      layout: {
        background: { color: COLORS.panel },
        textColor: COLORS.textDim,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#232a38' },
        horzLines: { color: '#232a38' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: COLORS.border },
      timeScale: { borderColor: COLORS.border, timeVisible: false, secondsVisible: false },
    });
    chartRef.current = chart;

    priceSeriesRef.current = chart.addSeries(LineSeries, {
      color: COLORS.accent, lineWidth: 2,
    });
    highlightSeriesRef.current = chart.addSeries(HistogramSeries, {
      color: 'rgba(16, 185, 129, 0.15)',
      priceFormat: { type: 'volume' },
      priceScaleId: 'highlight_overlay',
    });
    chart.priceScale('highlight_overlay').applyOptions({
      visible: false,
      scaleMargins: { top: 0, bottom: 0 },
    });
    hvSeriesRef.current = chart.addSeries(LineSeries, {
      color: '#f59e0b', lineWidth: 1, lastValueVisible: true, priceLineVisible: false,
      title: 'HV',
    }, 1);

    slopeSeriesRef.current = chart.addSeries(LineSeries, {
      color: '#a855f7', lineWidth: 1, lastValueVisible: true, priceLineVisible: false,
      title: 'Slope',
    }, 2);

    consolSeriesRef.current = chart.addSeries(LineSeries, {
      color: '#10b981', lineWidth: 1, lastValueVisible: true, priceLineVisible: false,
      title: 'Consol',
    }, 3);

    const resize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      // Defer removal to the next frame so any pending paint completes first
      requestAnimationFrame(() => {
        try { chart.remove(); } catch { /* already disposed */ }
      });
      chartRef.current = null;
    };
  }, []);

  // Load indicator bars (price + HV + slope + consol + cell_id) when symbol changes
  useEffect(() => {
    if (!selectedSymbol || !priceSeriesRef.current) return;
    tercileLinesAdded.current = false;
    apiService
      .getComboAnalyticsIndicatorBars(selectedSymbol)
      .then((res) => {
        indicatorBarsRef.current = res.bars;
        setBarsVersion((v) => v + 1);
        priceSeriesRef.current?.setData(
          res.bars.map((b) => ({ time: b.date as Time, value: b.close }))
        );
        highlightSeriesRef.current?.setData([]);
        hvSeriesRef.current?.setData(
          res.bars.map((b) => ({ time: b.date as Time, value: b.hv_20 }))
        );
        slopeSeriesRef.current?.setData(
          res.bars.map((b) => ({ time: b.date as Time, value: b.slope_12w * 100 }))
        );
        consolSeriesRef.current?.setData(
          res.bars.map((b) => ({ time: b.date as Time, value: b.consol_z }))
        );
        // Add tercile threshold lines (once per series)
        if (!tercileLinesAdded.current && res.terciles) {
          const lineStyle = 1; // dashed
          const lineWidth = 1;
          const hvCuts = res.terciles['hv_20'];
          const slopeCuts = res.terciles['slope_12w'];
          const consolCuts = res.terciles['consol_zscore'];
          if (hvSeriesRef.current && hvCuts) {
            hvSeriesRef.current.createPriceLine({ price: hvCuts[0], color: '#f59e0b55', lineWidth, lineStyle, title: 'LO|MED', axisLabelVisible: false });
            hvSeriesRef.current.createPriceLine({ price: hvCuts[1], color: '#f59e0b55', lineWidth, lineStyle, title: 'MED|HI', axisLabelVisible: false });
          }
          if (slopeSeriesRef.current && slopeCuts) {
            slopeSeriesRef.current.createPriceLine({ price: slopeCuts[0] * 100, color: '#a855f755', lineWidth, lineStyle, title: 'DN|FL', axisLabelVisible: false });
            slopeSeriesRef.current.createPriceLine({ price: slopeCuts[1] * 100, color: '#a855f755', lineWidth, lineStyle, title: 'FL|UP', axisLabelVisible: false });
          }
          if (consolSeriesRef.current && consolCuts) {
            consolSeriesRef.current.createPriceLine({ price: consolCuts[0], color: '#10b98155', lineWidth, lineStyle, title: 'EXP|NRM', axisLabelVisible: false });
            consolSeriesRef.current.createPriceLine({ price: consolCuts[1], color: '#10b98155', lineWidth, lineStyle, title: 'NRM|TGT', axisLabelVisible: false });
          }
          tercileLinesAdded.current = true;
        }
        chartRef.current?.timeScale().fitContent();
      })
      .catch((err) => console.error('Failed to load indicator bars', err));
  }, [selectedSymbol]);

  // Highlight dates matching the hovered cell — green for wins, red for losses
  useEffect(() => {
    if (!highlightSeriesRef.current) return;
    if (!hoveredCombo) {
      highlightSeriesRef.current.setData([]);
      return;
    }
    const data: HistogramData[] = indicatorBarsRef.current.map((b) => {
      const match = b.cell_id === hoveredCombo;
      const win = b.pnl !== null && b.pnl > 0;
      return {
        time: b.date as Time,
        value: match ? 1 : 0,
        color: match
          ? (b.pnl === null ? 'rgba(148, 163, 184, 0.15)' : win ? 'rgba(16, 185, 129, 0.20)' : 'rgba(239, 68, 68, 0.20)')
          : 'rgba(0,0,0,0)',
      };
    });
    highlightSeriesRef.current.setData(data);
  }, [hoveredCombo]);

  const handleHover = useCallback((cellId: string | null) => {
    setHoveredCombo(cellId);
  }, []);

  // Builder: compute the selected cell_id and match it to highlight + stats
  const builderCellId = useMemo(() => {
    if (!builderHV || !builderSlope || !builderConsol) return null;
    return `${builderHV}_${builderSlope}_${builderConsol}`;
  }, [builderHV, builderSlope, builderConsol]);

  const builderCell = useMemo(() => {
    if (!builderCellId) return null;
    return cellSetups.find((c) => c.cell_id === builderCellId) ?? null;
  }, [builderCellId, cellSetups]);

  // Auto-highlight builder cell on the chart, also compute partial matches
  const builderPartialCellId = useMemo(() => {
    const parts: string[] = [];
    if (builderHV) parts.push(builderHV);
    if (builderSlope) parts.push(builderSlope);
    if (builderConsol) parts.push(builderConsol);
    return parts.length > 0 ? parts : null;
  }, [builderHV, builderSlope, builderConsol]);

  useEffect(() => {
    if (subTab !== 'builder') return;
    if (builderCellId) {
      setHoveredCombo(builderCellId);
    } else {
      setHoveredCombo(null);
    }
  }, [subTab, builderCellId]);

  // For partial selections (e.g. only HV=HI), highlight matching cells on price pane
  useEffect(() => {
    if (subTab !== 'builder' || !highlightSeriesRef.current) return;
    if (builderCellId) return; // full match handled by hoveredCombo effect
    if (!builderPartialCellId) {
      highlightSeriesRef.current.setData([]);
      return;
    }
    const data: HistogramData[] = indicatorBarsRef.current.map((b) => {
      const parts = b.cell_id.split('_');
      let match = true;
      if (builderHV && parts[0] !== builderHV) match = false;
      if (builderSlope && parts[1] !== builderSlope) match = false;
      if (builderConsol && parts[2] !== builderConsol) match = false;
      const win = b.pnl !== null && b.pnl > 0;
      return {
        time: b.date as Time,
        value: match ? 1 : 0,
        color: match
          ? (b.pnl === null ? 'rgba(148, 163, 184, 0.15)' : win ? 'rgba(16, 185, 129, 0.20)' : 'rgba(239, 68, 68, 0.20)')
          : 'rgba(0,0,0,0)',
      };
    });
    highlightSeriesRef.current.setData(data);
  }, [subTab, builderPartialCellId, builderHV, builderSlope, builderConsol, builderCellId]);

  // Compute stats for partial selections (any combo of 1-3 axes)
  const builderStats = useMemo(() => {
    if (!builderPartialCellId || indicatorBarsRef.current.length === 0) return null;
    // We need P&L data — the indicatorBarsRef doesn't have it. Use cellSetups instead
    // For partial matches, sum across matching cells.
    const matchingCells = cellSetups.filter((c) => {
      if (c.label === '?') return false;
      const parts = c.cell_id.split('_');
      if (builderHV && parts[0] !== builderHV) return false;
      if (builderSlope && parts[1] !== builderSlope) return false;
      if (builderConsol && parts[2] !== builderConsol) return false;
      return true;
    });
    if (matchingCells.length === 0) return null;
    const totalN = matchingCells.reduce((s, c) => s + c.n, 0);
    const avgPnl = matchingCells.reduce((s, c) => s + (c.avg_pnl ?? 0) * c.n, 0) / totalN;
    const avgWin = matchingCells.reduce((s, c) => s + (c.win_rate ?? 0) * c.n, 0) / totalN;
    const avgDelta = matchingCells.reduce((s, c) => s + (c.delta_pnl ?? 0) * c.n, 0) / totalN;
    return { n: totalN, avg_pnl: avgPnl, win_rate: avgWin, delta_pnl: avgDelta, n_cells: matchingCells.length };
  }, [builderPartialCellId, builderHV, builderSlope, builderConsol, cellSetups]);

  const trustedCells = useMemo(() =>
    cellSetups.filter((c) => c.label !== '?' && c.delta_pnl !== null),
    [cellSetups]
  );
  const untrustedCells = useMemo(() =>
    cellSetups.filter((c) => c.label === '?' || c.delta_pnl === null),
    [cellSetups]
  );
  // Trusted first (sorted), then untrusted (sorted by N desc)
  const byPnlCells = useMemo(() => [
    ...[...trustedCells].sort((a, b) => (b.avg_pnl ?? 0) - (a.avg_pnl ?? 0)),
    ...[...untrustedCells].sort((a, b) => b.n - a.n),
  ], [trustedCells, untrustedCells]);
  const byVolumeCells = useMemo(() => [
    ...[...trustedCells].sort((a, b) => b.n - a.n),
    ...[...untrustedCells].sort((a, b) => b.n - a.n),
  ], [trustedCells, untrustedCells]);
  const activeList = subTab === 'bypnl' ? byPnlCells : byVolumeCells;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Symbol picker */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
        <label style={{ color: COLORS.textDim, fontSize: 13 }}>Symbol:</label>
        <select
          value={selectedSymbol}
          onChange={(e) => onSelectSymbol(e.target.value)}
          style={{
            padding: '6px 12px',
            background: COLORS.panel,
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 6,
            fontSize: 14,
            minWidth: 120,
          }}
        >
          {symbols.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {baseline && (
          <div style={{ marginLeft: 'auto', color: COLORS.textDim, fontSize: 13 }}>
            Baseline: <b style={{ color: COLORS.text }}>{fmtPct(baseline.pnl)}</b> P&L,{' '}
            <b style={{ color: COLORS.text }}>{baseline.win.toFixed(1)}%</b> win rate · N={baseline.n}
            {' · '}{trustedCells.length}/27 cells
          </div>
        )}
      </div>

      {/* Chart + lists layout — fills remaining height */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 380px',
          gap: 16,
        }}
      >
        {/* Chart panel */}
        <div
          style={{
            background: COLORS.panel,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div style={{
            color: COLORS.textDim,
            fontSize: 12,
            marginBottom: 6,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            {hoveredCombo ? (
              <>
                <span>Highlighting:</span>
                <SetupIcon cellId={hoveredCombo} size={32} />
                <b style={{ color: COLORS.text }}>{displayCell(hoveredCombo)}</b>
                <span style={{ color: COLORS.textMuted, fontSize: 11 }}>
                  ({hoveredCombo.replace(/_/g, ' · ')})
                </span>
              </>
            ) : (
              'Hover a setup on the right to highlight active dates'
            )}
          </div>
          <div ref={chartContainerRef} style={{ flex: 1, minHeight: 0, width: '100%' }} />
        </div>

        {/* Setups panel with sub-tabs */}
        <div
          style={{
            background: COLORS.panel,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* Sub-tab header */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
            <button
              onClick={() => setSubTab('bypnl')}
              style={{
                flex: 1,
                padding: '10px 6px',
                background: subTab === 'bypnl' ? '#1f2a24' : 'transparent',
                color: subTab === 'bypnl' ? COLORS.good : COLORS.textDim,
                border: 'none',
                borderBottom: subTab === 'bypnl' ? `2px solid ${COLORS.good}` : '2px solid transparent',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              By P&L
            </button>
            <button
              onClick={() => setSubTab('byvolume')}
              style={{
                flex: 1,
                padding: '10px 6px',
                background: subTab === 'byvolume' ? '#1e2830' : 'transparent',
                color: subTab === 'byvolume' ? '#60a5fa' : COLORS.textDim,
                border: 'none',
                borderBottom: subTab === 'byvolume' ? `2px solid #60a5fa` : '2px solid transparent',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              By N
            </button>
            <button
              onClick={() => setSubTab('builder')}
              style={{
                flex: 1,
                padding: '10px 6px',
                background: subTab === 'builder' ? '#1e2436' : 'transparent',
                color: subTab === 'builder' ? COLORS.accent : COLORS.textDim,
                border: 'none',
                borderBottom: subTab === 'builder' ? `2px solid ${COLORS.accent}` : '2px solid transparent',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              Explorer
            </button>
          </div>

          {/* Body */}
          {subTab === 'builder' ? (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px' }}>
              {/* Axis selectors */}
              {([
                { label: 'Volatility (HV 20d)', options: HV_OPTIONS, labels: HV_LABELS, value: builderHV, set: setBuilderHV, color: '#f59e0b' },
                { label: 'Slope (12w)', options: SLOPE_OPTIONS, labels: SLOPE_LABELS, value: builderSlope, set: setBuilderSlope, color: '#a855f7' },
                { label: 'Volatility Contraction (VCI)', options: CONSOL_OPTIONS, labels: CONSOL_LABELS, value: builderConsol, set: setBuilderConsol, color: '#10b981' },
              ] as const).map(({ label, options, labels, value, set, color }) => (
                <div key={label} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {label}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {options.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => set(value === opt ? null : opt)}
                        style={{
                          flex: 1,
                          padding: '6px 4px',
                          fontSize: 11,
                          fontWeight: value === opt ? 700 : 400,
                          borderRadius: 6,
                          border: `1px solid ${value === opt ? color : COLORS.border}`,
                          background: value === opt ? `${color}22` : 'transparent',
                          color: value === opt ? color : COLORS.textDim,
                          cursor: 'pointer',
                        }}
                      >
                        {labels[opt]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Stats panel */}
              <div style={{
                marginTop: 8,
                padding: 10,
                background: '#131826',
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
              }}>
                {builderPartialCellId && builderStats ? (
                  <>
                    <div style={{ marginBottom: 6 }}>
                      <div style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: COLORS.text,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}>
                        {builderCellId && <SetupIcon cellId={builderCellId} size={30} />}
                        {builderCellId
                          ? displayCell(builderCellId)
                          : `${builderStats.n_cells} matching cells`}
                      </div>
                      {builderCellId && (
                        <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>
                          {builderCellId.replace(/_/g, ' · ')}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 11 }}>
                      <div>
                        <div style={{ color: COLORS.textMuted, fontSize: 9 }}>N</div>
                        <div style={{ color: COLORS.text, fontWeight: 600 }}>{builderStats.n}</div>
                      </div>
                      <div>
                        <div style={{ color: COLORS.textMuted, fontSize: 9 }}>Win rate</div>
                        <div style={{ color: COLORS.text, fontWeight: 600 }}>{builderStats.win_rate.toFixed(1)}%</div>
                      </div>
                      <div>
                        <div style={{ color: COLORS.textMuted, fontSize: 9 }}>Avg P&L</div>
                        <div style={{
                          color: builderStats.avg_pnl >= 0 ? COLORS.good : COLORS.bad,
                          fontWeight: 700,
                        }}>
                          {builderStats.avg_pnl >= 0 ? '+' : ''}{builderStats.avg_pnl.toFixed(2)}%
                        </div>
                      </div>
                      <div>
                        <div style={{ color: COLORS.textMuted, fontSize: 9 }}>Δ vs baseline</div>
                        <div style={{
                          color: builderStats.delta_pnl >= 0 ? COLORS.good : COLORS.bad,
                          fontWeight: 600,
                        }}>
                          {builderStats.delta_pnl >= 0 ? '+' : ''}{builderStats.delta_pnl.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                    {builderCell && (
                      <div style={{ marginTop: 8, fontSize: 10, color: COLORS.textMuted }}>
                        Label: <span style={{
                          color: builderCell.label === '+' ? COLORS.good : builderCell.label === '-' ? COLORS.bad : COLORS.textDim,
                          fontWeight: 600,
                        }}>{builderCell.label === '?' ? 'untrusted (N < 30)' : builderCell.label}</span>
                        {builderCell.is_current && <span style={{ color: COLORS.accent, marginLeft: 8 }}>Current setup</span>}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ color: COLORS.textMuted, fontSize: 11, fontStyle: 'italic' }}>
                    Select at least one axis to see stats. Partial selections aggregate across matching cells.
                  </div>
                )}
              </div>
            </div>
          ) : (
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {loading && (
              <div style={{ padding: 12, color: COLORS.textMuted, fontSize: 12 }}>Loading…</div>
            )}
            {!loading && activeList.length === 0 && (
              <div style={{ padding: 12, color: COLORS.textMuted, fontSize: 12 }}>No trusted cells</div>
            )}
            {activeList.map((c) => {
              const isUntrusted = c.label === '?' || c.delta_pnl === null;
              const pnl = c.avg_pnl ?? 0;
              const delta = c.delta_pnl ?? 0;
              const isBad = pnl < 0;
              const isExpanded = expandedCell === c.cell_id;
              return (
                <div
                  key={c.cell_id}
                  style={{
                    borderBottom: `1px solid ${COLORS.border}`,
                    background: c.is_current ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                    borderLeft: c.is_current ? `3px solid ${COLORS.accent}` : '3px solid transparent',
                    opacity: isUntrusted ? 0.5 : 1,
                  }}
                >
                  <div
                    onMouseEnter={() => handleHover(c.cell_id)}
                    onMouseLeave={() => handleHover(null)}
                    onClick={() => setExpandedCell(isExpanded ? null : c.cell_id)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: COLORS.text,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                      title={c.cell_id.replace(/_/g, ' · ')}
                      >
                        <span style={{
                          fontSize: 9,
                          color: COLORS.textMuted,
                          display: 'inline-block',
                          width: 8,
                        }}>{isExpanded ? '▾' : '▸'}</span>
                        <SetupIcon cellId={c.cell_id} size={28} />
                        {displayCell(c.cell_id)}
                        {c.is_current && (
                          <span style={{
                            fontSize: 9,
                            background: `${COLORS.accent}22`,
                            color: COLORS.accent,
                            padding: '1px 5px',
                            borderRadius: 3,
                            fontWeight: 700,
                          }}>NOW</span>
                        )}
                        {isUntrusted && (
                          <span style={{
                            fontSize: 9,
                            background: 'rgba(107, 114, 128, 0.2)',
                            color: COLORS.textMuted,
                            padding: '1px 5px',
                            borderRadius: 3,
                            fontWeight: 600,
                          }}>LOW N</span>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>
                        N={c.n}
                        {!isUntrusted && (
                          <>
                            {' · win '}{c.win_rate != null ? `${c.win_rate.toFixed(0)}%` : '--'}
                            {' · Δ '}{c.delta_pnl != null ? `${delta >= 0 ? '+' : ''}${delta.toFixed(2)}%` : '--'}
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {isUntrusted ? (
                        <div style={{ fontSize: 10, color: COLORS.textMuted, fontStyle: 'italic' }}>
                          insufficient data
                        </div>
                      ) : (
                        <>
                          <div style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: isBad ? COLORS.bad : COLORS.good,
                          }}>
                            {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}%
                          </div>
                          <div style={{ fontSize: 9, color: COLORS.textMuted }}>
                            {c.win_rate != null ? `${c.win_rate.toFixed(0)}% win` : ''}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: '4px 12px 12px 12px' }}>
                      <PnlHistogram
                        cellId={c.cell_id}
                        bars={indicatorBarsRef.current}
                        barsVersion={barsVersion}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// the MECE 3×3×3 fingerprint cells instead of arbitrary factor combos.

// ═══════════════════════════════════════════════════════════════════════════
// Matrix View Tab
// ═══════════════════════════════════════════════════════════════════════════

const MatrixTab: React.FC = () => {
  const [data, setData] = useState<MatrixResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService
      .getComboAnalyticsMatrix({ factor_count: 2, min_samples: 30, top_per_stock: 10, n_columns: 5 })
      .then((res) => setData(res))
      .catch((err) => console.error('Failed to load matrix', err))
      .finally(() => setLoading(false));
  }, []);

  const columns = useMemo(() => {
    if (!data) return [];
    return [
      ...data.top_columns.map((c) => ({ ...c, kind: 'top' as const })),
      ...data.bottom_columns.map((c) => ({ ...c, kind: 'bottom' as const })),
    ];
  }, [data]);

  const cellColor = (v: number | null): string => {
    if (v === null || v === undefined) return COLORS.textMuted;
    const mag = Math.min(1, Math.abs(v) / 4); // cap at 4pp
    if (v >= 0) {
      const alpha = 0.15 + mag * 0.45;
      return `rgba(16, 185, 129, ${alpha})`;
    } else {
      const alpha = 0.15 + mag * 0.45;
      return `rgba(239, 68, 68, ${alpha})`;
    }
  };

  if (loading) return <div style={{ color: COLORS.textMuted }}>Loading matrix…</div>;
  if (!data) return <div style={{ color: COLORS.bad }}>Failed to load matrix</div>;

  return (
    <div
      style={{
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: 16,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ color: COLORS.textDim, fontSize: 12, marginBottom: 12, flexShrink: 0 }}>
        Columns are the 2-factor combos that most frequently appear in the per-stock top-10 (green) and bottom-10 (red)
        across all 20 symbols. Cells show <b>delta P&L vs stock baseline</b> in percentage points. Last column is the
        stock's raw baseline (not a delta).
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  color: COLORS.textDim,
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 3,
                }}
              >
                Symbol
              </th>
              {columns.map((c) => (
                <th
                  key={c.combo}
                  style={{
                    textAlign: 'center',
                    padding: '8px 10px',
                    borderBottom: `2px solid ${COLORS.border}`,
                    borderLeft: `3px solid ${c.kind === 'top' ? COLORS.good : COLORS.bad}`,
                    color: c.kind === 'top' ? COLORS.good : COLORS.bad,
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                    position: 'sticky',
                    top: 0,
                    background: COLORS.panel,
                    zIndex: 2,
                  }}
                  title={`${c.combo} · ${c.appearances}/20 appearances`}
                >
                  {c.combo}
                  <div style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 400 }}>
                    in {c.appearances}/20
                  </div>
                </th>
              ))}
              <th
                style={{
                  textAlign: 'center',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  borderLeft: `3px solid ${COLORS.accent}`,
                  color: COLORS.accent,
                  whiteSpace: 'nowrap',
                  position: 'sticky',
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 2,
                }}
              >
                Baseline P&L
              </th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.symbol}>
                <td
                  style={{
                    padding: '8px 10px',
                    borderBottom: `1px solid ${COLORS.border}`,
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    background: COLORS.panel,
                    zIndex: 1,
                  }}
                >
                  {row.symbol}
                </td>
                {columns.map((c) => {
                  const cell = row.cells[c.combo];
                  return (
                    <td
                      key={c.combo}
                      style={{
                        padding: '8px 10px',
                        borderBottom: `1px solid ${COLORS.border}`,
                        textAlign: 'center',
                        background: cell ? cellColor(cell.delta_pnl) : 'transparent',
                        color: cell ? COLORS.text : COLORS.textMuted,
                      }}
                      title={
                        cell
                          ? `${c.combo}\nΔP&L: ${fmtPct(cell.delta_pnl)}\nAvg P&L: ${fmtPct(
                              cell.avg_pnl_pct
                            )}\nWin: ${cell.win_rate_pct.toFixed(1)}%\nN: ${cell.n_samples}`
                          : 'No data (below min sample threshold)'
                      }
                    >
                      {cell ? fmtPct(cell.delta_pnl, 1) : '—'}
                    </td>
                  );
                })}
                <td
                  style={{
                    padding: '8px 10px',
                    borderBottom: `1px solid ${COLORS.border}`,
                    textAlign: 'center',
                    color: row.baseline_pnl >= 0 ? COLORS.good : COLORS.bad,
                    fontWeight: 500,
                  }}
                >
                  {fmtPct(row.baseline_pnl, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Regimes View Tab
// ═══════════════════════════════════════════════════════════════════════════

interface RegimeCell {
  n_samples: number;
  avg_pnl_pct: number;
  win_rate_pct: number;
  delta_pnl: number;
  delta_win: number;
}

interface RegimeRow {
  symbol: string;
  baseline_pnl: number;
  baseline_win: number;
  n_total: number;
  current_regime: string | null;
  cells: Record<string, RegimeCell | null>;
}

interface RegimesResponse {
  regime_order: string[];
  definitions: Record<string, { label: string; rule: string; description: string }>;
  rows: RegimeRow[];
}

const RegimesTab: React.FC = () => {
  const [data, setData] = useState<RegimesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredRegime, setHoveredRegime] = useState<string | null>(null);

  useEffect(() => {
    apiService
      .getComboAnalyticsRegimes()
      .then((res) => setData(res))
      .catch((err) => console.error('Failed to load regimes', err))
      .finally(() => setLoading(false));
  }, []);

  const cellColor = (v: number | null): string => {
    if (v === null || v === undefined) return 'transparent';
    const mag = Math.min(1, Math.abs(v) / 3); // cap at 3pp
    const alpha = 0.18 + mag * 0.5;
    if (v >= 0) return `rgba(16, 185, 129, ${alpha})`;
    return `rgba(239, 68, 68, ${alpha})`;
  };

  if (loading) return <div style={{ color: COLORS.textMuted }}>Loading regimes…</div>;
  if (!data) return <div style={{ color: COLORS.bad }}>Failed to load regimes</div>;

  return (
    <div
      style={{
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: 16,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ color: COLORS.textDim, fontSize: 12, marginBottom: 12, flexShrink: 0 }}>
        Each bar in the 5-year backtest is assigned to exactly one of 7 mutually-exclusive regimes
        (evaluated top-down, first match wins). Cells show <b>Δ P&L vs the stock baseline</b>. The
        bold-bordered cell in each row is the stock's <b>current</b> regime as of the latest bar —
        so you can see at a glance whether each stock is currently sitting in a historically
        profitable or unprofitable regime. Hover the column headers for the exact rule.
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  color: COLORS.textDim,
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 3,
                }}
              >
                Symbol
              </th>
              {data.regime_order.map((reg) => {
                const def = data.definitions[reg];
                return (
                  <th
                    key={reg}
                    onMouseEnter={() => setHoveredRegime(reg)}
                    onMouseLeave={() => setHoveredRegime(null)}
                    style={{
                      textAlign: 'center',
                      padding: '8px 10px',
                      borderBottom: `2px solid ${COLORS.border}`,
                      color: COLORS.text,
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                      position: 'sticky',
                      top: 0,
                      background: COLORS.panel,
                      zIndex: 2,
                      cursor: 'help',
                    }}
                  >
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      {def?.label || reg}
                      <div style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 400, marginTop: 2 }}>
                        {def?.rule}
                      </div>
                      {hoveredRegime === reg && def && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginTop: 8,
                            width: 280,
                            padding: '10px 12px',
                            background: '#0b0f17',
                            border: `1px solid ${COLORS.accent}`,
                            borderRadius: 6,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                            color: COLORS.text,
                            fontSize: 11,
                            fontWeight: 400,
                            lineHeight: 1.5,
                            textTransform: 'none',
                            letterSpacing: 0,
                            textAlign: 'left',
                            zIndex: 20,
                            pointerEvents: 'none',
                          }}
                        >
                          <div style={{ color: COLORS.accent, fontWeight: 600, marginBottom: 4 }}>
                            {def.label}
                          </div>
                          <div style={{ color: COLORS.textDim, fontSize: 10, marginBottom: 6 }}>
                            Rule: {def.rule}
                          </div>
                          <div>{def.description}</div>
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
              <th
                style={{
                  textAlign: 'center',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  borderLeft: `3px solid ${COLORS.accent}`,
                  color: COLORS.accent,
                  whiteSpace: 'nowrap',
                  position: 'sticky',
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 2,
                  fontWeight: 600,
                }}
              >
                Baseline P&L
              </th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.symbol}>
                <td
                  style={{
                    padding: '8px 10px',
                    borderBottom: `1px solid ${COLORS.border}`,
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    background: COLORS.panel,
                    zIndex: 1,
                  }}
                >
                  {row.symbol}
                </td>
                {data.regime_order.map((reg) => {
                  const cell = row.cells[reg];
                  const isCurrent = row.current_regime === reg;
                  const bg = cell ? cellColor(cell.delta_pnl) : 'transparent';
                  return (
                    <td
                      key={reg}
                      style={{
                        padding: '8px 10px',
                        borderBottom: `1px solid ${COLORS.border}`,
                        textAlign: 'center',
                        background: bg,
                        color: cell ? COLORS.text : COLORS.textMuted,
                        outline: isCurrent ? `2px solid ${COLORS.accent}` : 'none',
                        outlineOffset: isCurrent ? -2 : 0,
                        position: 'relative',
                      }}
                      title={
                        cell
                          ? `${reg}\nΔ P&L: ${fmtPct(cell.delta_pnl, 2)}\nAvg P&L: ${fmtPct(
                              cell.avg_pnl_pct
                            )}\nWin: ${cell.win_rate_pct.toFixed(1)}%\nN: ${cell.n_samples}${
                              isCurrent ? '\n★ CURRENT REGIME' : ''
                            }`
                          : 'No samples in this regime'
                      }
                    >
                      {cell ? (
                        <div style={{ lineHeight: 1.1 }}>
                          <div style={{ fontWeight: isCurrent ? 700 : 500 }}>
                            {fmtPct(cell.delta_pnl, 1)}
                          </div>
                          <div style={{ fontSize: 9, color: COLORS.textMuted }}>N={cell.n_samples}</div>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                  );
                })}
                <td
                  style={{
                    padding: '8px 10px',
                    borderBottom: `1px solid ${COLORS.border}`,
                    textAlign: 'center',
                    borderLeft: `3px solid ${COLORS.accent}`,
                    color: row.baseline_pnl >= 0 ? COLORS.good : COLORS.bad,
                    fontWeight: 600,
                  }}
                  title={`Baseline over ${row.n_total} bars · Win ${row.baseline_win.toFixed(1)}%`}
                >
                  {fmtPct(row.baseline_pnl, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Regime Tree Tab — per-stock decision-tree regime classifier
// ═══════════════════════════════════════════════════════════════════════════

interface TreeLeaf {
  leaf_id: number;
  rule: string;
  rule_parts: Array<{ feature: string; op: string; threshold: number }>;
  n_samples: number;
  avg_pnl_pct: number;
  win_rate_pct: number;
  delta_pnl: number;
  delta_win: number;
}

interface StockTree {
  baseline_pnl: number;
  baseline_win: number;
  n_total: number;
  feature_importance: Record<string, number>;
  leaves: TreeLeaf[];
  current_leaf_id: number | null;
}

interface RegimeTreeResponse {
  features: string[];
  depth: number;
  stocks: Record<string, StockTree>;
}

const RegimeTreeTab: React.FC = () => {
  const [data, setData] = useState<RegimeTreeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');

  useEffect(() => {
    apiService
      .getComboAnalyticsRegimeTree()
      .then((res) => {
        setData(res);
        const firstSym = Object.keys(res.stocks).sort()[0];
        if (firstSym) setSelectedSymbol(firstSym);
      })
      .catch((err) => console.error('Failed to load regime tree', err))
      .finally(() => setLoading(false));
  }, []);

  const cellColor = (v: number | null): string => {
    if (v === null || v === undefined) return 'transparent';
    const mag = Math.min(1, Math.abs(v) / 5);
    const alpha = 0.18 + mag * 0.5;
    if (v >= 0) return `rgba(16, 185, 129, ${alpha})`;
    return `rgba(239, 68, 68, ${alpha})`;
  };

  if (loading) return <div style={{ color: COLORS.textMuted }}>Loading regime tree…</div>;
  if (!data) return <div style={{ color: COLORS.bad }}>Failed to load regime tree</div>;

  const stock = data.stocks[selectedSymbol];
  const symbols = Object.keys(data.stocks).sort();

  const sortedFeatures = stock
    ? Object.entries(stock.feature_importance).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div
      style={{
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: 16,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ color: COLORS.textDim, fontSize: 12, marginBottom: 12, flexShrink: 0 }}>
        Per-stock depth-{data.depth} regression tree on <code>{data.features.join(', ')}</code> predicting{' '}
        <code>pnl_no_stop_pct</code>. Each stock gets its own 5–8 leaves because the tree picks different splits per
        stock. 5-fold CV confirmed this framework captures ~2× the test R² of the 3×3 grid (+0.053 R² lift, 95% CI
        [+0.037, +0.069], t=6.45).
      </div>

      {/* Stock picker + baseline row */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          marginBottom: 12,
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ color: COLORS.textDim, fontSize: 12 }}>Symbol:</label>
          <select
            value={selectedSymbol}
            onChange={(e) => setSelectedSymbol(e.target.value)}
            style={{
              padding: '6px 10px',
              background: COLORS.bg,
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 4,
              fontSize: 12,
            }}
          >
            {symbols.map((sym) => (
              <option key={sym} value={sym}>
                {sym}
              </option>
            ))}
          </select>
        </div>
        {stock && (
          <>
            <div style={{ color: COLORS.textDim, fontSize: 12 }}>
              Baseline P&L:{' '}
              <span style={{ color: stock.baseline_pnl >= 0 ? COLORS.good : COLORS.bad, fontWeight: 600 }}>
                {fmtPct(stock.baseline_pnl, 2)}
              </span>
              {'  ·  '}
              Win rate: <span style={{ color: COLORS.text, fontWeight: 600 }}>{stock.baseline_win.toFixed(1)}%</span>
              {'  ·  '}
              N: <span style={{ color: COLORS.text, fontWeight: 600 }}>{stock.n_total}</span>
              {'  ·  '}
              {stock.current_leaf_id !== null && (
                <>
                  Current leaf:{' '}
                  <span style={{ color: COLORS.accent, fontWeight: 600 }}>#{stock.current_leaf_id}</span>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Feature importance row */}
      {stock && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            marginBottom: 12,
            flexShrink: 0,
            fontSize: 11,
            color: COLORS.textDim,
          }}
        >
          <span>Feature importance:</span>
          {sortedFeatures.map(([feat, imp]) => {
            const pct = imp * 100;
            return (
              <span
                key={feat}
                style={{
                  padding: '2px 8px',
                  background: `rgba(59, 130, 246, ${0.1 + imp * 0.4})`,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 10,
                  color: COLORS.text,
                }}
              >
                <code style={{ color: COLORS.accent }}>{feat}</code> {pct.toFixed(0)}%
              </span>
            );
          })}
        </div>
      )}

      {/* Leaves table */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'center',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  color: COLORS.textDim,
                  position: 'sticky',
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 2,
                  width: 50,
                }}
              >
                Leaf
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  color: COLORS.textDim,
                  position: 'sticky',
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 2,
                }}
              >
                Rule
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  color: COLORS.textDim,
                  position: 'sticky',
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 2,
                  width: 70,
                }}
              >
                N
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  color: COLORS.textDim,
                  position: 'sticky',
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 2,
                  width: 100,
                }}
              >
                Avg P&L
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  color: COLORS.textDim,
                  position: 'sticky',
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 2,
                  width: 120,
                }}
              >
                Δ P&L
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  color: COLORS.textDim,
                  position: 'sticky',
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 2,
                  width: 90,
                }}
              >
                Win rate
              </th>
              <th
                style={{
                  textAlign: 'right',
                  padding: '8px 10px',
                  borderBottom: `2px solid ${COLORS.border}`,
                  color: COLORS.textDim,
                  position: 'sticky',
                  top: 0,
                  background: COLORS.panel,
                  zIndex: 2,
                  width: 90,
                }}
              >
                Δ Win
              </th>
            </tr>
          </thead>
          <tbody>
            {stock?.leaves.map((leaf) => {
              const isCurrent = leaf.leaf_id === stock.current_leaf_id;
              return (
                <tr
                  key={leaf.leaf_id}
                  style={{
                    background: isCurrent ? `rgba(59, 130, 246, 0.12)` : 'transparent',
                    outline: isCurrent ? `2px solid ${COLORS.accent}` : 'none',
                    outlineOffset: isCurrent ? -2 : 0,
                  }}
                >
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'center',
                      fontWeight: isCurrent ? 700 : 500,
                      color: isCurrent ? COLORS.accent : COLORS.textDim,
                    }}
                  >
                    #{leaf.leaf_id}
                    {isCurrent && <div style={{ fontSize: 9 }}>★ NOW</div>}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      color: COLORS.text,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                    }}
                  >
                    {leaf.rule}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      color: COLORS.text,
                    }}
                  >
                    {leaf.n_samples}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      color: leaf.avg_pnl_pct >= 0 ? COLORS.good : COLORS.bad,
                      fontWeight: 500,
                    }}
                  >
                    {fmtPct(leaf.avg_pnl_pct, 2)}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      background: cellColor(leaf.delta_pnl),
                      color: COLORS.text,
                      fontWeight: 600,
                    }}
                  >
                    {fmtPct(leaf.delta_pnl, 2)}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      color: COLORS.text,
                    }}
                  >
                    {leaf.win_rate_pct.toFixed(1)}%
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      color: leaf.delta_win >= 0 ? COLORS.good : COLORS.bad,
                      fontWeight: 500,
                    }}
                  >
                    {leaf.delta_win >= 0 ? '+' : ''}
                    {leaf.delta_win.toFixed(1)}pp
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Regime GBR Tab — gradient-boosted-trees benchmark vs grid/tree
// ═══════════════════════════════════════════════════════════════════════════

interface GbrStockStats {
  n_total: number;
  baseline_pnl: number;
  baseline_win: number;
  grid_cv_r2_mean: number;
  grid_cv_r2_std: number;
  tree_cv_r2_mean: number;
  tree_cv_r2_std: number;
  gbr_cv_r2_mean: number;
  gbr_cv_r2_std: number;
  feature_importance: Record<string, number>;
  current_prediction_pct: number | null;
  current_features: Record<string, number> | null;
  current_delta_vs_baseline: number | null;
}

interface GbrResponse {
  features: string[];
  tree_features: string[];
  gbr_params: Record<string, any>;
  n_folds: number;
  stocks: Record<string, GbrStockStats>;
  aggregate: {
    grid_mean_r2: number;
    tree_mean_r2: number;
    gbr_mean_r2: number;
    gbr_vs_tree_lift: number;
    gbr_vs_grid_lift: number;
    n_stocks: number;
  };
}

const RegimeGbrTab: React.FC = () => {
  const [data, setData] = useState<GbrResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<'symbol' | 'gbr_cv_r2_mean' | 'current_delta_vs_baseline'>('gbr_cv_r2_mean');

  useEffect(() => {
    apiService
      .getComboAnalyticsRegimeGbr()
      .then((res) => setData(res))
      .catch((err) => console.error('Failed to load regime gbr', err))
      .finally(() => setLoading(false));
  }, []);

  const sortedStocks = useMemo(() => {
    if (!data) return [];
    const entries = Object.entries(data.stocks);
    return entries.sort(([symA, a], [symB, b]) => {
      if (sortKey === 'symbol') return symA.localeCompare(symB);
      if (sortKey === 'gbr_cv_r2_mean') return b.gbr_cv_r2_mean - a.gbr_cv_r2_mean;
      // current_delta_vs_baseline — nulls last
      const av = a.current_delta_vs_baseline ?? -Infinity;
      const bv = b.current_delta_vs_baseline ?? -Infinity;
      return bv - av;
    });
  }, [data, sortKey]);

  if (loading) return <div style={{ color: COLORS.textMuted }}>Loading gradient boosted model (training 20 per-stock models)…</div>;
  if (!data) return <div style={{ color: COLORS.bad }}>Failed to load regime gbr</div>;

  const agg = data.aggregate;

  // Bar chart proportions — scale against the max R² (gbr)
  const maxR2 = Math.max(agg.gbr_mean_r2, agg.tree_mean_r2, agg.grid_mean_r2, 0.01);
  const barWidth = (r2: number) => `${Math.max(0, Math.min(100, (r2 / maxR2) * 100))}%`;

  return (
    <div
      style={{
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: 16,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Caveat banner */}
      <div
        style={{
          background: 'rgba(245, 158, 11, 0.10)',
          border: '1px solid rgba(245, 158, 11, 0.35)',
          borderRadius: 6,
          padding: '8px 12px',
          marginBottom: 12,
          fontSize: 11,
          color: '#fbbf24',
          flexShrink: 0,
          lineHeight: 1.5,
        }}
      >
        <strong>⚠️ Caveat:</strong> GBR test R² under random 5-fold CV is likely inflated by forward-window
        autocorrelation (bars 35 days apart share ~97% of their P&L outcome). Boosted trees have enough capacity
        to memorize through this temporal proximity, while the simpler grid and single tree cannot. Use these
        numbers for <strong>within-protocol cross-model comparison</strong>, not as absolute generalization
        estimates. The GBR's <em>true</em> generalization R² is probably in the 0.15–0.25 range — still
        meaningfully better than the tree (~0.10), but nowhere near the +0.29 lift shown here. Framework choice is
        also a tradeoff: GBR is more accurate but has no interpretable leaves.
      </div>

      {/* Aggregate R² chart */}
      <div
        style={{
          background: '#0f1420',
          border: `1px solid ${COLORS.border}`,
          borderRadius: 6,
          padding: 12,
          marginBottom: 12,
          flexShrink: 0,
        }}
      >
        <div style={{ color: COLORS.textDim, fontSize: 11, marginBottom: 8 }}>
          Aggregate 5-fold CV mean R² across {agg.n_stocks} stocks (under random split — see caveat)
        </div>
        {[
          { label: '3×3 grid', value: agg.grid_mean_r2, color: '#6b7280' },
          { label: 'Single tree (d3, 4 feat)', value: agg.tree_mean_r2, color: COLORS.accent },
          { label: 'GBR HistGBT (d4, 100 iter, 8 feat)', value: agg.gbr_mean_r2, color: COLORS.good },
        ].map((row) => (
          <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ width: 220, fontSize: 11, color: COLORS.textDim }}>{row.label}</div>
            <div style={{ flex: 1, height: 18, background: '#1a1f2b', borderRadius: 3, position: 'relative' }}>
              <div
                style={{
                  width: barWidth(row.value),
                  height: '100%',
                  background: row.color,
                  borderRadius: 3,
                  transition: 'width 0.3s',
                }}
              />
            </div>
            <div style={{ width: 70, fontSize: 12, color: COLORS.text, fontWeight: 600, textAlign: 'right' }}>
              {row.value.toFixed(4)}
            </div>
          </div>
        ))}
        <div style={{ marginTop: 8, fontSize: 11, color: COLORS.textDim }}>
          <span style={{ color: COLORS.good, fontWeight: 600 }}>GBR lift over tree:</span>{' '}
          <span style={{ color: COLORS.text, fontWeight: 600 }}>{agg.gbr_vs_tree_lift >= 0 ? '+' : ''}{agg.gbr_vs_tree_lift.toFixed(4)}</span>
          {'  ·  '}
          <span style={{ color: COLORS.good, fontWeight: 600 }}>GBR lift over grid:</span>{' '}
          <span style={{ color: COLORS.text, fontWeight: 600 }}>{agg.gbr_vs_grid_lift >= 0 ? '+' : ''}{agg.gbr_vs_grid_lift.toFixed(4)}</span>
          {'  ·  '}
          <span style={{ color: COLORS.textMuted }}>Features:</span>{' '}
          <code style={{ color: COLORS.accent }}>{data.features.join(', ')}</code>
        </div>
      </div>

      {/* Sort controls */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexShrink: 0, alignItems: 'center' }}>
        <span style={{ color: COLORS.textDim, fontSize: 11 }}>Sort by:</span>
        {(
          [
            { id: 'gbr_cv_r2_mean', label: 'GBR R²' },
            { id: 'current_delta_vs_baseline', label: "Today's Δ forecast" },
            { id: 'symbol', label: 'Symbol' },
          ] as Array<{ id: typeof sortKey; label: string }>
        ).map((opt) => (
          <button
            key={opt.id}
            onClick={() => setSortKey(opt.id)}
            style={{
              padding: '4px 10px',
              fontSize: 11,
              border: `1px solid ${sortKey === opt.id ? COLORS.accent : COLORS.border}`,
              background: sortKey === opt.id ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
              color: sortKey === opt.id ? COLORS.accent : COLORS.textDim,
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Per-stock table */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr>
              {[
                { label: 'Symbol', align: 'left' },
                { label: 'Baseline', align: 'right' },
                { label: 'Grid R²', align: 'right' },
                { label: 'Tree R²', align: 'right' },
                { label: 'GBR R²', align: 'right' },
                { label: "Today's pred", align: 'right' },
                { label: 'Δ vs base', align: 'right' },
                { label: 'Top features (permutation importance)', align: 'left' },
              ].map((h) => (
                <th
                  key={h.label}
                  style={{
                    textAlign: h.align as any,
                    padding: '8px 10px',
                    borderBottom: `2px solid ${COLORS.border}`,
                    color: COLORS.textDim,
                    position: 'sticky',
                    top: 0,
                    background: COLORS.panel,
                    zIndex: 2,
                    fontWeight: 600,
                  }}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedStocks.map(([sym, s]) => {
              const topFeatures = Object.entries(s.feature_importance)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3);
              const gbrBest =
                s.gbr_cv_r2_mean > s.tree_cv_r2_mean && s.gbr_cv_r2_mean > s.grid_cv_r2_mean;
              const treeBest =
                s.tree_cv_r2_mean > s.gbr_cv_r2_mean && s.tree_cv_r2_mean > s.grid_cv_r2_mean;
              return (
                <tr key={sym}>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      fontWeight: 600,
                      color: COLORS.text,
                    }}
                  >
                    {sym}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      color: s.baseline_pnl >= 0 ? COLORS.good : COLORS.bad,
                    }}
                    title={`Baseline over ${s.n_total} bars · Win ${s.baseline_win.toFixed(1)}%`}
                  >
                    {fmtPct(s.baseline_pnl, 2)}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      color: COLORS.textDim,
                    }}
                  >
                    {s.grid_cv_r2_mean.toFixed(4)}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      color: treeBest ? COLORS.accent : COLORS.textDim,
                      fontWeight: treeBest ? 700 : 400,
                    }}
                  >
                    {s.tree_cv_r2_mean.toFixed(4)}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      color: gbrBest ? COLORS.good : COLORS.text,
                      fontWeight: gbrBest ? 700 : 500,
                      background: gbrBest ? 'rgba(16, 185, 129, 0.12)' : 'transparent',
                    }}
                  >
                    {s.gbr_cv_r2_mean.toFixed(4)}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      color:
                        s.current_prediction_pct === null
                          ? COLORS.textMuted
                          : s.current_prediction_pct >= 0
                            ? COLORS.good
                            : COLORS.bad,
                      fontWeight: 600,
                    }}
                  >
                    {s.current_prediction_pct === null ? '—' : fmtPct(s.current_prediction_pct, 2)}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      textAlign: 'right',
                      color:
                        s.current_delta_vs_baseline === null
                          ? COLORS.textMuted
                          : s.current_delta_vs_baseline >= 0
                            ? COLORS.good
                            : COLORS.bad,
                      fontWeight: 500,
                    }}
                  >
                    {s.current_delta_vs_baseline === null
                      ? '—'
                      : `${s.current_delta_vs_baseline >= 0 ? '+' : ''}${s.current_delta_vs_baseline.toFixed(2)}pp`}
                  </td>
                  <td
                    style={{
                      padding: '8px 10px',
                      borderBottom: `1px solid ${COLORS.border}`,
                    }}
                  >
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {topFeatures.map(([feat, imp]) => (
                        <span
                          key={feat}
                          style={{
                            padding: '2px 6px',
                            background: `rgba(59, 130, 246, ${0.1 + imp * 0.5})`,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 10,
                            color: COLORS.text,
                            fontSize: 10,
                          }}
                        >
                          <code style={{ color: COLORS.accent }}>{feat}</code> {(imp * 100).toFixed(0)}%
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Fingerprint Tab — 3×3×3 cube + per-stock fingerprints + clustering
// ═══════════════════════════════════════════════════════════════════════════

interface FingerprintCell {
  n: number;
  avg_pnl: number | null;
  win_rate: number | null;
  delta_pnl: number | null;
  delta_win: number | null;
  label: '+' | '-' | '0' | '?';
}

interface FingerprintStock {
  baseline_pnl: number;
  baseline_win: number;
  n_total: number;
  n_trusted_cells: number;
  coverage_pct: number;
  cells: Record<string, FingerprintCell>;
  current_cell: string | null;
  fingerprint: string;
}

interface FingerprintResponse {
  features: string[];
  universal_cuts: Record<string, [number, number]>;
  hv_bins: string[];
  slope_bins: string[];
  consol_bins: string[];
  all_cell_ids: string[];
  thresholds: { min_cell_n: number; win_delta_threshold_pp: number };
  stocks: Record<string, FingerprintStock>;
  clustering: {
    n_clusters: number;
    assignments: Record<string, number>;
    summaries: Array<{
      cluster_id: number;
      members: string[];
      size: number;
      consensus_fingerprint: Record<string, string>;
    }>;
    distances: Record<string, Record<string, number>>;
  };
}

const FINGERPRINT_LABEL_COLORS: Record<string, { bg: string; fg: string }> = {
  '+': { bg: 'rgba(16, 185, 129, 0.35)', fg: '#10b981' },
  '-': { bg: 'rgba(239, 68, 68, 0.35)', fg: '#ef4444' },
  '0': { bg: 'rgba(148, 163, 184, 0.15)', fg: '#94a3b8' },
  '?': {
    bg: 'repeating-linear-gradient(45deg, rgba(107, 114, 128, 0.1) 0 4px, rgba(107, 114, 128, 0.2) 4px 8px)',
    fg: '#6b7280',
  },
};

const FingerprintTab: React.FC = () => {
  const [data, setData] = useState<FingerprintResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [matrixSlope, setMatrixSlope] = useState<string>('FLAT');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [clusterFilter, setClusterFilter] = useState<number | 'all'>('all');

  useEffect(() => {
    apiService
      .getComboAnalyticsFingerprint()
      .then((res) => {
        setData(res);
        const first = Object.keys(res.stocks).sort()[0];
        if (first) setSelectedSymbol(first);
      })
      .catch((err) => console.error('Failed to load fingerprint', err))
      .finally(() => setLoading(false));
  }, []);

  // Order stocks by cluster so that clustered stocks appear together in the matrix,
  // then filter by search input and cluster filter.
  const orderedStocks = useMemo(() => {
    if (!data) return [];
    const symbols = Object.keys(data.stocks);
    const sorted = symbols.slice().sort((a, b) => {
      const ca = data.clustering.assignments[a] ?? 99;
      const cb = data.clustering.assignments[b] ?? 99;
      if (ca !== cb) return ca - cb;
      return a.localeCompare(b);
    });
    const search = searchFilter.trim().toUpperCase();
    return sorted.filter((sym) => {
      if (clusterFilter !== 'all' && data.clustering.assignments[sym] !== clusterFilter) return false;
      if (search && !sym.includes(search)) return false;
      return true;
    });
  }, [data, searchFilter, clusterFilter]);

  const cellColor = (v: number | null): string => {
    if (v === null || v === undefined) return 'transparent';
    const mag = Math.min(1, Math.abs(v) / 3);
    const alpha = 0.18 + mag * 0.55;
    if (v >= 0) return `rgba(16, 185, 129, ${alpha})`;
    return `rgba(239, 68, 68, ${alpha})`;
  };

  if (loading) return <div style={{ color: COLORS.textMuted }}>Loading fingerprints…</div>;
  if (!data) return <div style={{ color: COLORS.bad }}>Failed to load fingerprints</div>;

  const stock = data.stocks[selectedSymbol];
  const symbols = Object.keys(data.stocks).sort();
  const clusterOf = (s: string) => data.clustering.assignments[s];
  const CLUSTER_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ef4444', '#06b6d4'];

  // Matrix columns: 9 (HV × consol) combinations, for the selected slope
  const matrixColumns: Array<{ hv: string; consol: string; cell_id: string }> = [];
  for (const hv of data.hv_bins) {
    for (const consol of data.consol_bins) {
      matrixColumns.push({ hv, consol, cell_id: `${hv}_${matrixSlope}_${consol}` });
    }
  }

  return (
    <div
      style={{
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: 16,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Description */}
      <div style={{ color: COLORS.textDim, fontSize: 11, marginBottom: 10, flexShrink: 0, lineHeight: 1.5 }}>
        <strong>Descriptive fingerprinting (not predictive).</strong> Each bar is classified into a 27-cell cube
        defined by <b>universal</b> terciles across all 20 pooled stocks: HV20 ({data.universal_cuts.hv_20[0]}/
        {data.universal_cuts.hv_20[1]}) × slope_12w ({data.universal_cuts.slope_12w[0]}/
        {data.universal_cuts.slope_12w[1]}) × consol_zscore ({data.universal_cuts.consol_zscore[0]}/
        {data.universal_cuts.consol_zscore[1]}). Cells with N &lt; {data.thresholds.min_cell_n} are labeled{' '}
        <code style={{ color: '#6b7280' }}>?</code>. Labels use Δ win rate vs stock baseline:{' '}
        <span style={{ color: '#10b981', fontWeight: 600 }}>+</span> &gt; +
        {data.thresholds.win_delta_threshold_pp}pp,{' '}
        <span style={{ color: '#ef4444', fontWeight: 600 }}>−</span> &lt; −{data.thresholds.win_delta_threshold_pp}pp,{' '}
        <span style={{ color: '#94a3b8', fontWeight: 600 }}>0</span> otherwise. Stocks are clustered by Hamming
        distance over the intersection of trusted cells.
      </div>

      {/* Stock picker + cluster chips */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          marginBottom: 12,
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <label style={{ color: COLORS.textDim, fontSize: 12 }}>Symbol:</label>
        <select
          value={selectedSymbol}
          onChange={(e) => setSelectedSymbol(e.target.value)}
          style={{
            padding: '6px 10px',
            background: COLORS.bg,
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          {symbols.map((sym) => (
            <option key={sym} value={sym}>
              {sym}  ·  cluster {data.clustering.assignments[sym]}
            </option>
          ))}
        </select>
        {stock && (
          <div style={{ fontSize: 11, color: COLORS.textDim }}>
            Baseline{' '}
            <span style={{ color: stock.baseline_pnl >= 0 ? COLORS.good : COLORS.bad, fontWeight: 600 }}>
              {fmtPct(stock.baseline_pnl, 2)}
            </span>
            {' · '}
            win <span style={{ color: COLORS.text, fontWeight: 600 }}>{stock.baseline_win.toFixed(1)}%</span>
            {' · '}
            N <span style={{ color: COLORS.text, fontWeight: 600 }}>{stock.n_total}</span>
            {' · '}
            coverage{' '}
            <span style={{ color: COLORS.text, fontWeight: 600 }}>
              {stock.n_trusted_cells}/27 ({stock.coverage_pct}%)
            </span>
            {' · '}
            cluster{' '}
            <span
              style={{
                padding: '2px 8px',
                background: `${CLUSTER_COLORS[clusterOf(selectedSymbol) - 1]}33`,
                border: `1px solid ${CLUSTER_COLORS[clusterOf(selectedSymbol) - 1]}`,
                color: CLUSTER_COLORS[clusterOf(selectedSymbol) - 1],
                borderRadius: 10,
                fontWeight: 600,
              }}
            >
              {clusterOf(selectedSymbol)}
            </span>
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {/* Isometric 3D Cube view */}
        {stock && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: COLORS.textDim, fontSize: 11, marginBottom: 8 }}>
              3×3×3 isometric cube — HV × Consolidation × Slope.{' '}
              {stock.current_cell && (
                <>
                  Current cell: <code style={{ color: COLORS.accent }}>{stock.current_cell}</code>
                </>
              )}
            </div>
            <FingerprintCube
              stock={stock}
              hvBins={data.hv_bins}
              slopeBins={data.slope_bins}
              consolBins={data.consol_bins}
            />
          </div>
        )}

        {/* Matrix view */}
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <div style={{ color: COLORS.textDim, fontSize: 11 }}>
              Cross-stock comparison —
              <b style={{ color: COLORS.text, marginLeft: 4 }}>
                {orderedStocks.length}/{Object.keys(data.stocks).length} shown
              </b>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {data.slope_bins.map((slope) => (
                <button
                  key={slope}
                  onClick={() => setMatrixSlope(slope)}
                  style={{
                    padding: '4px 12px',
                    fontSize: 11,
                    background: matrixSlope === slope ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                    color: matrixSlope === slope ? COLORS.accent : COLORS.textDim,
                    border: `1px solid ${matrixSlope === slope ? COLORS.accent : COLORS.border}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Slope {slope}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Search symbol…"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              style={{
                padding: '4px 10px',
                fontSize: 11,
                background: COLORS.bg,
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 4,
                width: 140,
              }}
            />
            <select
              value={clusterFilter === 'all' ? 'all' : String(clusterFilter)}
              onChange={(e) =>
                setClusterFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))
              }
              style={{
                padding: '4px 8px',
                fontSize: 11,
                background: COLORS.bg,
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 4,
              }}
            >
              <option value="all">All clusters</option>
              {data.clustering.summaries.map((cl) => (
                <option key={cl.cluster_id} value={cl.cluster_id}>
                  Cluster {cl.cluster_id} ({cl.size})
                </option>
              ))}
            </select>
            {(searchFilter || clusterFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchFilter('');
                  setClusterFilter('all');
                }}
                style={{
                  padding: '4px 10px',
                  fontSize: 11,
                  background: 'transparent',
                  color: COLORS.textDim,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            )}
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '6px 8px',
                    borderBottom: `2px solid ${COLORS.border}`,
                    color: COLORS.textDim,
                    background: COLORS.panel,
                  }}
                >
                  Stock
                </th>
                <th
                  style={{
                    textAlign: 'center',
                    padding: '6px 8px',
                    borderBottom: `2px solid ${COLORS.border}`,
                    color: COLORS.textDim,
                    background: COLORS.panel,
                    fontSize: 10,
                  }}
                >
                  Clu
                </th>
                {matrixColumns.map((col) => (
                  <th
                    key={col.cell_id}
                    style={{
                      textAlign: 'center',
                      padding: '6px 4px',
                      borderBottom: `2px solid ${COLORS.border}`,
                      color: COLORS.textDim,
                      background: COLORS.panel,
                      fontSize: 9,
                      whiteSpace: 'nowrap',
                    }}
                    title={`HV=${col.hv}, slope=${matrixSlope}, consol=${col.consol}`}
                  >
                    <div>{col.hv}</div>
                    <div style={{ color: COLORS.textMuted }}>{col.consol.slice(0, 3)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orderedStocks.map((sym) => {
                const s = data.stocks[sym];
                const cluster = clusterOf(sym);
                const clusterColor = CLUSTER_COLORS[cluster - 1];
                const isSelected = sym === selectedSymbol;
                return (
                  <tr
                    key={sym}
                    onClick={() => setSelectedSymbol(sym)}
                    style={{
                      cursor: 'pointer',
                      background: isSelected ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                    }}
                  >
                    <td
                      style={{
                        padding: '4px 8px',
                        borderBottom: `1px solid ${COLORS.border}`,
                        fontWeight: 600,
                        color: isSelected ? COLORS.accent : COLORS.text,
                      }}
                    >
                      {sym}
                    </td>
                    <td
                      style={{
                        padding: '4px 8px',
                        borderBottom: `1px solid ${COLORS.border}`,
                        textAlign: 'center',
                        color: clusterColor,
                        fontWeight: 600,
                      }}
                    >
                      {cluster}
                    </td>
                    {matrixColumns.map((col) => {
                      const cell = s.cells[col.cell_id];
                      const isCurrent = s.current_cell === col.cell_id;
                      const isTrusted = cell.label !== '?';
                      const bg = isTrusted ? cellColor(cell.delta_pnl) : 'transparent';
                      const fg = isTrusted
                        ? (cell.delta_pnl !== null && cell.delta_pnl >= 0 ? '#10b981' : '#ef4444')
                        : COLORS.textMuted;
                      const tooltip = !isTrusted
                        ? `${col.cell_id}\nN=${cell.n} (below trust threshold)`
                        : `${col.cell_id}\nN=${cell.n}\nAvg P&L: ${cell.avg_pnl !== null ? fmtPct(cell.avg_pnl, 2) : '—'}\nWin rate: ${cell.win_rate !== null ? cell.win_rate.toFixed(1) + '%' : '—'}\nΔ P&L: ${cell.delta_pnl !== null ? fmtPct(cell.delta_pnl, 2) : '—'}\nΔ Win: ${cell.delta_win !== null ? (cell.delta_win >= 0 ? '+' : '') + cell.delta_win.toFixed(1) + 'pp' : '—'}${isCurrent ? '\n★ CURRENT' : ''}`;
                      const display = !isTrusted
                        ? '?'
                        : cell.delta_pnl === null
                          ? '—'
                          : `${cell.delta_pnl >= 0 ? '+' : ''}${cell.delta_pnl.toFixed(1)}`;
                      const nScale = isTrusted ? Math.max(0, Math.min(1, (cell.n - 30) / (170))) : 0;
                      const fontSize = isTrusted ? 7 + nScale * 10 : 11;
                      return (
                        <td
                          key={col.cell_id}
                          title={tooltip}
                          style={{
                            padding: 0,
                            borderBottom: `1px solid ${COLORS.border}`,
                            textAlign: 'center',
                            background: bg,
                            minWidth: 40,
                            outline: isCurrent ? `2px solid ${COLORS.accent}` : 'none',
                            outlineOffset: isCurrent ? -2 : 0,
                            position: 'relative',
                          }}
                        >
                          <div
                            style={{
                              height: 22,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: fg,
                              fontWeight: 700,
                              fontSize,
                              lineHeight: 1,
                            }}
                          >
                            {display}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Cluster summaries */}
        <div style={{ marginTop: 16 }}>
          <div style={{ color: COLORS.textDim, fontSize: 11, marginBottom: 8 }}>
            Cluster assignments (Ward linkage on Hamming distance, {data.clustering.n_clusters} clusters):
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.clustering.summaries.map((cl) => {
              const clusterColor = CLUSTER_COLORS[cl.cluster_id - 1];
              return (
                <div
                  key={cl.cluster_id}
                  style={{
                    padding: '8px 12px',
                    background: '#0f1420',
                    border: `1px solid ${clusterColor}55`,
                    borderLeft: `3px solid ${clusterColor}`,
                    borderRadius: 4,
                    fontSize: 11,
                  }}
                >
                  <div style={{ color: clusterColor, fontWeight: 600, marginBottom: 3 }}>
                    Cluster {cl.cluster_id} · {cl.size} stocks
                  </div>
                  <div style={{ color: COLORS.text }}>{cl.members.join(', ')}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── 3×3 Setup Matrix (HV × Slope, 3 consol bars per cell) ─── */
const FingerprintCube: React.FC<{
  stock: FingerprintStock;
  hvBins: string[];
  slopeBins: string[];
  consolBins: string[];
}> = ({ stock, hvBins, slopeBins, consolBins }) => {
  const [hovered, setHovered] = useState<string | null>(null);
  const [showLowN, setShowLowN] = useState(false);

  const isTrusted = (c: FingerprintCell) => c.label !== '?';

  const valColor = (cell: FingerprintCell, forceShow = false) => {
    if ((!isTrusted(cell) && !forceShow) || cell.avg_pnl === null) return COLORS.textMuted;
    return cell.avg_pnl >= 0 ? '#10b981' : '#ef4444';
  };

  const barBg = (cell: FingerprintCell, forceShow = false) => {
    if ((!isTrusted(cell) && !forceShow) || cell.avg_pnl === null) return 'rgba(30, 35, 50, 0.5)';
    const v = cell.avg_pnl;
    const alpha = 0.15 + Math.min(1, Math.abs(v) / 3) * 0.50;
    return v >= 0 ? `rgba(16, 185, 129, ${alpha})` : `rgba(239, 68, 68, ${alpha})`;
  };

  const fmtVal = (cell: FingerprintCell, forceShow = false) => {
    if (!isTrusted(cell) && !forceShow) return '?';
    if (cell.avg_pnl === null) return '—';
    return fmtPct(cell.avg_pnl, 1);
  };

  const hoveredCell = hovered ? stock.cells[hovered] : null;
  const CONSOL_LABEL: Record<string, string> = { TIGHT: 'Tight', NORMAL: 'Normal', EXPANDING: 'Expand' };
  const SLOPE_LABEL: Record<string, string> = { DOWN: 'Slope ↓', FLAT: 'Slope →', UP: 'Slope ↑' };
  const HV_LABEL: Record<string, string> = { LO: 'HV Lo', MED: 'HV Med', HI: 'HV Hi' };

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      {/* 3×3 matrix */}
      <div>
        <button
          onClick={() => setShowLowN((v) => !v)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 10, color: showLowN ? COLORS.text : COLORS.textMuted,
            background: showLowN ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
            border: `1px solid ${showLowN ? COLORS.accent : COLORS.border}`,
            borderRadius: 4, padding: '3px 8px', cursor: 'pointer',
            marginBottom: 6, transition: 'all 0.2s',
          }}
        >
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: showLowN ? COLORS.accent : COLORS.textMuted,
            transition: 'background 0.2s',
          }} />
          Low-sample cells {showLowN ? 'shown' : 'hidden'}
        </button>
        <table style={{ borderCollapse: 'separate', borderSpacing: 6 }}>
          <thead>
            <tr>
              <th />
              {slopeBins.map((slope) => (
                <th key={slope} style={{
                  fontSize: 10, color: COLORS.textDim, fontWeight: 600,
                  textAlign: 'center', paddingBottom: 4, letterSpacing: 0.5,
                }}>
                  {SLOPE_LABEL[slope] ?? slope}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hvBins.map((hv) => (
              <tr key={hv}>
                {/* HV label + consol sub-labels */}
                <td style={{
                  fontSize: 10, color: COLORS.textDim, fontWeight: 600,
                  textAlign: 'right', paddingRight: 4, verticalAlign: 'middle',
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                    {consolBins.map((consol, ci) => (
                      <div key={consol} style={{
                        display: 'flex', alignItems: 'center', gap: 6, height: 16,
                      }}>
                        {ci === 1 && (
                          <span style={{ fontSize: 10, color: COLORS.textDim, fontWeight: 600 }}>{HV_LABEL[hv] ?? hv}</span>
                        )}
                        <span style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 500, minWidth: 40, textAlign: 'right' }}>
                          {CONSOL_LABEL[consol] ?? consol}
                        </span>
                      </div>
                    ))}
                  </div>
                </td>
                {slopeBins.map((slope) => {
                  // 3 consol sub-cells for this HV × Slope combination
                  const cells = consolBins.map((consol) => ({
                    id: `${hv}_${slope}_${consol}`,
                    consol,
                    cell: stock.cells[`${hv}_${slope}_${consol}`],
                  }));
                  const hasCurrent = cells.some((c) => stock.current_cell === c.id);

                  return (
                    <td key={slope} style={{
                      background: '#0f1420',
                      border: hasCurrent ? `2px solid ${COLORS.accent}` : `1px solid ${COLORS.border}`,
                      borderRadius: 6, padding: '5px 3px', verticalAlign: 'top',
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 80 }}>
                        {cells.map(({ id, consol, cell }) => {
                          const isCurrent = stock.current_cell === id;
                          const isHov = hovered === id;
                          const hidden = !showLowN && !isTrusted(cell);

                          return (
                            <div
                              key={consol}
                              onMouseEnter={() => !hidden && setHovered(id)}
                              onMouseLeave={() => setHovered(null)}
                              style={{
                                display: 'flex', alignItems: 'center',
                                background: isHov && !hidden ? 'rgba(255,255,255,0.04)' : 'transparent',
                                borderRadius: 3, padding: '0 4px',
                                cursor: hidden ? 'default' : 'pointer',
                                transition: 'background 0.15s, opacity 0.2s',
                                borderLeft: isCurrent ? `3px solid ${COLORS.accent}` : '3px solid transparent',
                                opacity: hidden ? 0.15 : 1,
                              }}
                            >
                              {/* Color bar */}
                              <div style={{
                                flex: 1, height: 16,
                                background: hidden ? 'rgba(30, 35, 50, 0.3)' : barBg(cell, showLowN),
                                borderRadius: 2, display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                              }}>
                                <span style={{
                                  fontSize: 11, fontWeight: 700,
                                  color: hidden ? 'transparent' : valColor(cell, showLowN), lineHeight: 1,
                                }}>
                                  {fmtVal(cell, showLowN)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {/* Minimal legend — only what's not obvious from labels */}
        <div style={{ marginTop: 6, marginLeft: 30, fontSize: 9, color: COLORS.textMuted }}>
          Cell values = Avg P&L ·
          <span style={{ color: '#10b981' }}> green</span> outperform ·
          <span style={{ color: '#ef4444' }}> red</span> underperform ·
          <span> ? = low sample</span>
        </div>
      </div>

      {/* Hover detail panel */}
      <div style={{
        width: 190, minHeight: 160,
        background: '#0f1420', border: `1px solid ${COLORS.border}`,
        borderRadius: 6, padding: 12, fontSize: 11,
        opacity: hovered ? 1 : 0.3,
        transition: 'opacity 0.2s',
      }}>
        {hovered && hoveredCell ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <SetupIcon cellId={hovered} size={28} color={valColor(hoveredCell)} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{displayCell(hovered)}</div>
                <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: 'var(--font-mono)' }}>{hovered}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', marginTop: 8 }}>
              <div style={{ color: COLORS.textDim }}>N</div>
              <div style={{ color: COLORS.text, fontWeight: 600, textAlign: 'right' }}>{hoveredCell.n}</div>
              <div style={{ color: COLORS.textDim }}>Avg P&L</div>
              <div style={{ color: hoveredCell.avg_pnl !== null && hoveredCell.avg_pnl >= 0 ? COLORS.good : COLORS.bad, fontWeight: 600, textAlign: 'right' }}>
                {hoveredCell.avg_pnl !== null ? fmtPct(hoveredCell.avg_pnl, 2) : '—'}
              </div>
              <div style={{ color: COLORS.textDim }}>Win rate</div>
              <div style={{ color: COLORS.text, fontWeight: 600, textAlign: 'right' }}>
                {hoveredCell.win_rate !== null ? `${hoveredCell.win_rate.toFixed(1)}%` : '—'}
              </div>
              <div style={{ color: COLORS.textDim }}>Δ P&L</div>
              <div style={{ color: valColor(hoveredCell), fontWeight: 600, textAlign: 'right' }}>
                {hoveredCell.delta_pnl !== null ? fmtPct(hoveredCell.delta_pnl, 2) : '—'}
              </div>
              <div style={{ color: COLORS.textDim }}>Δ Win</div>
              <div style={{ color: hoveredCell.delta_win !== null && hoveredCell.delta_win >= 0 ? COLORS.good : COLORS.bad, fontWeight: 600, textAlign: 'right' }}>
                {hoveredCell.delta_win !== null ? `${hoveredCell.delta_win >= 0 ? '+' : ''}${hoveredCell.delta_win.toFixed(1)}pp` : '—'}
              </div>
              <div style={{ color: COLORS.textDim }}>Label</div>
              <div style={{
                textAlign: 'right', fontWeight: 700,
                color: hoveredCell.label === '+' ? COLORS.good : hoveredCell.label === '-' ? COLORS.bad : COLORS.textMuted,
              }}>
                {hoveredCell.label}
              </div>
            </div>
            {stock.current_cell === hovered && (
              <div style={{
                marginTop: 8, padding: '3px 8px', textAlign: 'center',
                background: `${COLORS.accent}22`, border: `1px solid ${COLORS.accent}`,
                borderRadius: 4, fontSize: 10, color: COLORS.accent, fontWeight: 600,
              }}>
                CURRENT SETUP
              </div>
            )}
          </>
        ) : (
          <div style={{ color: COLORS.textMuted, fontSize: 10, textAlign: 'center', marginTop: 40 }}>
            Hover a cell to see details
          </div>
        )}
      </div>
    </div>
  );
};

const FingerprintSquare: React.FC<{
  slope: string;
  stock: FingerprintStock;
  hvBins: string[];
  consolBins: string[];
}> = ({ slope, stock, hvBins, consolBins }) => {
  return (
    <div
      style={{
        background: '#0f1420',
        border: `1px solid ${COLORS.border}`,
        borderRadius: 6,
        padding: 10,
      }}
    >
      <div
        style={{
          textAlign: 'center',
          color: COLORS.textDim,
          fontSize: 11,
          fontWeight: 600,
          marginBottom: 8,
          letterSpacing: 0.5,
        }}
      >
        Slope {slope}
      </div>
      <div style={{ display: 'flex' }}>
        {/* Y-axis label column */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            marginRight: 6,
            fontSize: 9,
            color: COLORS.textMuted,
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            textAlign: 'center',
            width: 12,
          }}
        >
          HV
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Column headers */}
          <div style={{ display: 'flex', gap: 3, marginLeft: 24 }}>
            {consolBins.map((consol) => (
              <div
                key={consol}
                style={{
                  width: 56,
                  fontSize: 9,
                  color: COLORS.textMuted,
                  textAlign: 'center',
                }}
              >
                {consol.slice(0, 3)}
              </div>
            ))}
          </div>
          {hvBins.map((hv) => (
            <div key={hv} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <div style={{ width: 20, fontSize: 9, color: COLORS.textMuted, textAlign: 'right' }}>{hv}</div>
              {consolBins.map((consol) => {
                const cell_id = `${hv}_${slope}_${consol}`;
                const cell = stock.cells[cell_id];
                const isCurrent = stock.current_cell === cell_id;
                const isTrusted = cell.label !== '?';
                const v = cell.delta_pnl;
                let bg = 'transparent';
                let fg = COLORS.textMuted;
                if (isTrusted && v !== null) {
                  const mag = Math.min(1, Math.abs(v) / 3);
                  const alpha = 0.18 + mag * 0.55;
                  bg = v >= 0 ? `rgba(16, 185, 129, ${alpha})` : `rgba(239, 68, 68, ${alpha})`;
                  fg = v >= 0 ? '#10b981' : '#ef4444';
                }
                const display = !isTrusted
                  ? '?'
                  : v === null
                    ? '—'
                    : `${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
                const tooltip = !isTrusted
                  ? `${cell_id}\nN=${cell.n} (below trust threshold)`
                  : `${cell_id}\nN=${cell.n}\nAvg P&L: ${cell.avg_pnl !== null ? fmtPct(cell.avg_pnl, 2) : '—'}\nWin rate: ${cell.win_rate !== null ? cell.win_rate.toFixed(1) + '%' : '—'}\nΔ P&L: ${cell.delta_pnl !== null ? fmtPct(cell.delta_pnl, 2) : '—'}\nΔ Win: ${cell.delta_win !== null ? (cell.delta_win >= 0 ? '+' : '') + cell.delta_win.toFixed(1) + 'pp' : '—'}${isCurrent ? '\n★ CURRENT' : ''}`;
                return (
                  <div
                    key={consol}
                    title={tooltip}
                    style={{
                      width: 56,
                      height: 52,
                      background: bg,
                      border: isCurrent ? `2px solid ${COLORS.accent}` : `1px solid ${COLORS.border}`,
                      borderRadius: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'help',
                    }}
                  >
                    <div style={{ fontSize: 15, fontWeight: 700, color: fg, lineHeight: 1 }}>
                      {display}
                    </div>
                    <div style={{ fontSize: 8, color: COLORS.textMuted, marginTop: 3 }}>
                      N={cell.n}
                    </div>
                    {isTrusted && cell.delta_win !== null && (
                      <div style={{ fontSize: 8, color: COLORS.textDim, marginTop: 1 }}>
                        {cell.delta_win >= 0 ? '+' : ''}
                        {cell.delta_win.toFixed(0)}pp
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComboAnalytics;
